/**
 * AccountForm Component
 * 
 * Reusable form for creating and editing accounts.
 * 
 * Features:
 * - Dynamic form fields based on account type
 * - Validation and error handling
 * - Type-specific field rendering
 * - WCAG 2.2 AA compliance
 * 
 * @see .specify/memory/constitution.md
 * @see /Reference/Reference design-review/design-principles.md
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Account, AccountType, CreateAccountPayload, UpdateAccountPayload, AssetCategory, AssetSubType, ValuationSource, LoanPurpose } from '../../types/account.types';
import { AccountTypeSelector } from './AccountTypeSelector';
import { createAccountSchema, updateAccountSchema } from '../../utils/account-validation';
import { AccountService } from '../../services/account-service';
import { hybridAccountService } from '../../services/hybrid-account-service';
import { ZodError } from 'zod';
import { ChevronDown, Info } from 'lucide-react';

interface AccountFormProps {
  account?: Account; // If provided, this is an edit form
  onSubmit: (data: CreateAccountPayload | UpdateAccountPayload) => void;
  onCancel: () => void;
  loading?: boolean;
  initialType?: AccountType; // Pre-select account type when adding new account
}

interface AccountFormState {
  name: string;
  type: AccountType;
  provider: string;
  accountNumber: string;
  openingBalance: number;
  openingDate: string;
  includeInNetWorth: boolean;
  notes: string;
  creditLimit?: number;
  interestRate?: number;
  paymentDueDate?: string;
  renewalDate?: string;
  loanAmount?: number;
  lastPaymentDue?: string;
  interestCalculatedDay?: number;
  loanPurpose?: LoanPurpose;
  linkedAccountId?: string | null;
  leaseEndDate?: string;
  residualValue?: number;
  employerName?: string;
  // Asset-specific fields
  category?: AssetCategory;
  subType?: AssetSubType;
  currentValue?: number;
  purchasePrice?: number;
  purchaseDate?: string;
  valuationSource?: ValuationSource;
  valuationProvider?: string;
  lastValuationDate?: string;
}

const LOAN_PURPOSE_LABELS: Record<LoanPurpose, string> = {
  [LoanPurpose.PERSONAL]: 'Personal',
  [LoanPurpose.CAR]: 'Car loan',
  [LoanPurpose.MORTGAGE]: 'Mortgage',
  [LoanPurpose.NOVATED_LEASE]: 'Novated lease',
  [LoanPurpose.OTHER]: 'Other',
};

export function AccountForm({
  account,
  onSubmit,
  onCancel,
  loading = false,
  initialType
}: AccountFormProps): React.ReactElement {
  const isEdit = !!account;
  const accountService = useMemo(() => new AccountService({
    baseUrl: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000',
    apiKey: 'test-key'
  }), []);

  const accountType = account?.type ?? initialType ?? AccountType.BANK_CASH;
  const isAssetType = accountType === AccountType.ASSETS;

  const initialState: AccountFormState = {
    name: account?.name ?? '',
    type: accountType,
    provider: account?.provider ?? '',
    // For assets, set default accountNumber to '0000' (required by validation but hidden)
    accountNumber: account?.accountNumber ?? (isAssetType ? '0000' : ''),
    // For assets, set openingBalance to 0 (required by validation but we use currentValue)
    openingBalance: account?.openingBalance ?? (isAssetType ? 0 : 0),
    openingDate: account?.openingDate ?? (isAssetType && account?.purchaseDate ? account.purchaseDate : new Date().toISOString().split('T')[0]),
    includeInNetWorth: account?.includeInNetWorth ?? true,
    notes: account?.notes ?? '',
    creditLimit: account?.creditLimit ?? undefined,
    interestRate: account?.interestRate ?? undefined,
    paymentDueDate: account?.paymentDueDate ?? undefined,
    renewalDate: account?.renewalDate ?? undefined,
    loanAmount: account?.loanAmount ?? undefined,
    lastPaymentDue: account?.lastPaymentDue ?? undefined,
    interestCalculatedDay: account?.interestCalculatedDay ?? undefined,
    loanPurpose: account?.loanPurpose ?? undefined,
    linkedAccountId: account?.linkedAccountId ?? undefined,
    leaseEndDate: account?.leaseEndDate ?? undefined,
    residualValue: account?.residualValue ?? undefined,
    employerName: account?.employerName ?? undefined,
    // Asset-specific fields
    category: account?.category ?? undefined,
    subType: account?.subType ?? undefined,
    currentValue: account?.currentValue ?? undefined,
    purchasePrice: account?.purchasePrice ?? undefined,
    purchaseDate: account?.purchaseDate ?? (isAssetType && account?.openingDate ? account.openingDate : undefined),
    valuationSource: account?.valuationSource ?? ValuationSource.MANUAL,
    valuationProvider: account?.valuationProvider ?? undefined,
    lastValuationDate: account?.lastValuationDate ?? undefined,
  };

  const [formData, setFormData] = useState<AccountFormState>(initialState);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableSubTypes, setAvailableSubTypes] = useState<AssetSubType[]>([]);
  const [vehicleAssets, setVehicleAssets] = useState<Account[]>([]);

  // Reset form when account type changes
  useEffect(() => {
    if (!isEdit) {
      setFormData(prev => ({
        ...prev,
        type: formData.type,
        creditLimit: undefined,
        interestRate: undefined,
        paymentDueDate: undefined,
        renewalDate: undefined,
        loanAmount: undefined,
        lastPaymentDue: undefined,
        interestCalculatedDay: undefined,
        loanPurpose: undefined,
        linkedAccountId: undefined,
        leaseEndDate: undefined,
        residualValue: undefined,
        employerName: undefined,
        // Reset asset fields when type changes
        category: undefined,
        subType: undefined,
        currentValue: undefined,
        purchasePrice: undefined,
        purchaseDate: undefined,
        valuationSource: ValuationSource.MANUAL,
        lastValuationDate: undefined,
        // Auto-set accountNumber for assets (required by validation, but hidden from user)
        accountNumber: formData.type === AccountType.ASSETS ? '0000' : prev.accountNumber,
        // For assets, set openingBalance to 0 (required by validation, but we use currentValue)
        openingBalance: formData.type === AccountType.ASSETS ? 0 : prev.openingBalance,
      }));
      setAvailableSubTypes([]);
    }
  }, [formData.type, isEdit]);

  // Load vehicle assets for novated lease linking
  useEffect(() => {
    if (formData.type !== AccountType.LOAN) {
      setVehicleAssets([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const response = await hybridAccountService.getAccounts({
          type: AccountType.ASSETS,
          status: 'active',
          limit: 100
        });
        if (cancelled || response.error || !response.data) return;
        const vehicles = response.data.accounts.filter(
          (acc) => acc.category === AssetCategory.VEHICLE
        );
        setVehicleAssets(vehicles);
      } catch {
        if (!cancelled) setVehicleAssets([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [formData.type]);

  // Fetch sub-types when category changes
  useEffect(() => {
    if (formData.type === AccountType.ASSETS && formData.category) {
      const subTypes = accountService.getSubTypesForCategory(formData.category);
      setAvailableSubTypes(subTypes);
      // Reset sub-type if it's not valid for the new category
      if (formData.subType && !subTypes.includes(formData.subType)) {
        setFormData(prev => ({ ...prev, subType: undefined }));
      }
    } else {
      setAvailableSubTypes([]);
    }
  }, [formData.type, formData.category, formData.subType, accountService]);

  const handleInputChange = <K extends keyof AccountFormState>(field: K, value: AccountFormState[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field as string]) {
      setErrors(prev => ({ ...prev, [field as string]: '' }));
    }
  };

  const handleNumericInputChange = (field: keyof AccountFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const parsedValue = rawValue === '' ? undefined : Number(rawValue);
    handleInputChange(field, (Number.isNaN(parsedValue as number) ? undefined : parsedValue) as AccountFormState[typeof field]);
  };

  const handleOptionalStringChange = (field: keyof AccountFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    handleInputChange(field, (rawValue === '' ? undefined : rawValue) as AccountFormState[typeof field]);
  };

  const handleRequiredNumberChange = (field: keyof AccountFormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;
    const parsedValue = Number(rawValue);
    handleInputChange(field, (Number.isNaN(parsedValue) ? 0 : parsedValue) as AccountFormState[typeof field]);
  };

  const validateForm = () => {
    const validationSchema = isEdit ? updateAccountSchema : createAccountSchema;

    // For asset updates, validate without openingDate so the 10-year bank rule never runs.
    // purchaseDate is the source of truth for assets.
    const dataToValidate =
      formData.type === AccountType.ASSETS
        ? (() => {
            const { openingDate, ...rest } = formData;
            return {
              ...rest,
              purchaseDate: formData.purchaseDate ?? openingDate,
              ...(isEdit ? {} : { openingDate: formData.purchaseDate ?? openingDate })
            };
          })()
        : formData;

    try {
      const validatedData = validationSchema.parse(dataToValidate);
      setErrors({});
      return validatedData;
    } catch (error) {
      const newErrors: Record<string, string> = {};
      if (error instanceof ZodError) {
        error.issues.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
      }
      // Surface purchaseDate errors under openingDate for the shared date field UI
      if (newErrors.purchaseDate && !newErrors.openingDate) {
        newErrors.openingDate = newErrors.purchaseDate;
      }
      setErrors(newErrors);
      
      // Scroll to first error field after a brief delay to ensure DOM is updated
      setTimeout(() => {
        const firstErrorField = document.querySelector('.border-red-500');
        if (firstErrorField) {
          firstErrorField.scrollIntoView({ behavior: 'smooth', block: 'center' });
          (firstErrorField as HTMLElement).focus();
        }
      }, 100);
      
      return null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    
    const validatedData = validateForm();
    
    
    if (validatedData) {
      // For assets, ensure purchaseDate is set from openingDate if not already set
      if (formData.type === AccountType.ASSETS && !formData.purchaseDate && formData.openingDate) {
        (validatedData as CreateAccountPayload | UpdateAccountPayload).purchaseDate = formData.openingDate;
      }
      // For assets, ensure accountNumber is set (required by validation)
      if (formData.type === AccountType.ASSETS && !validatedData.accountNumber) {
        validatedData.accountNumber = '0000';
      }
      // For assets, ensure openingBalance is set (required by validation, but we use currentValue)
      if (formData.type === AccountType.ASSETS && validatedData.openingBalance === undefined) {
        validatedData.openingBalance = 0;
      }
      
      // For assets, explicitly ensure asset fields are included in the payload
      if (formData.type === AccountType.ASSETS) {
        const assetPayload = validatedData as CreateAccountPayload | UpdateAccountPayload;
        // Ensure category, subType, and currentValue are explicitly set from formData
        if (formData.category) {
          assetPayload.category = formData.category;
        }
        if (formData.subType) {
          assetPayload.subType = formData.subType;
        }
        if (formData.currentValue !== undefined && formData.currentValue !== null) {
          assetPayload.currentValue = formData.currentValue;
        }
        if (formData.purchasePrice !== undefined && formData.purchasePrice !== null) {
          assetPayload.purchasePrice = formData.purchasePrice;
        }
        if (formData.purchaseDate) {
          assetPayload.purchaseDate = formData.purchaseDate;
        } else if (formData.openingDate) {
          assetPayload.purchaseDate = formData.openingDate;
        }
        if (formData.valuationSource) {
          assetPayload.valuationSource = formData.valuationSource;
        }
        if (formData.valuationProvider) {
          assetPayload.valuationProvider = formData.valuationProvider;
        }
        if (formData.lastValuationDate) {
          assetPayload.lastValuationDate = formData.lastValuationDate;
        }
        // On edit, only send purchaseDate (not openingDate) for assets
        if (isEdit) {
          delete (assetPayload as UpdateAccountPayload).openingDate;
        }
      }

      // Include loan purpose / lease fields for loans
      if (formData.type === AccountType.LOAN) {
        const loanPayload = validatedData as CreateAccountPayload | UpdateAccountPayload;
        if (formData.loanPurpose) {
          loanPayload.loanPurpose = formData.loanPurpose;
        }
        if (formData.linkedAccountId !== undefined) {
          loanPayload.linkedAccountId = formData.linkedAccountId || null;
        }
        if (formData.leaseEndDate) {
          loanPayload.leaseEndDate = formData.leaseEndDate;
        }
        if (formData.residualValue !== undefined && formData.residualValue !== null) {
          loanPayload.residualValue = formData.residualValue;
        }
        if (formData.employerName) {
          loanPayload.employerName = formData.employerName;
        }
      }
      
      
      onSubmit(validatedData);
    }
  };

  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case AccountType.CREDIT_CARD:
        return (
          <>
            <div>
              <label htmlFor="creditLimit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Credit Limit
              </label>
              <input
                type="number"
                id="creditLimit"
                value={formData.creditLimit ?? ''}
                onChange={handleNumericInputChange('creditLimit')}
                className={`input ${errors.creditLimit ? 'border-red-500' : ''}`}
                placeholder="Enter credit limit"
              />
              {errors.creditLimit && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.creditLimit}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Interest Rate (%)
              </label>
              <input
                type="number"
                id="interestRate"
                value={formData.interestRate ?? ''}
                onChange={handleNumericInputChange('interestRate')}
                className={`input ${errors.interestRate ? 'border-red-500' : ''}`}
                placeholder="Enter interest rate"
                step="0.01"
                min="0"
                max="100"
              />
              {errors.interestRate && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.interestRate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="paymentDueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Payment Due Date
              </label>
              <input
                type="date"
                id="paymentDueDate"
                value={formData.paymentDueDate ?? ''}
                onChange={handleOptionalStringChange('paymentDueDate')}
                className={`input ${errors.paymentDueDate ? 'border-red-500' : ''}`}
              />
              {errors.paymentDueDate && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.paymentDueDate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="renewalDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Renewal Date
              </label>
              <input
                type="date"
                id="renewalDate"
                value={formData.renewalDate ?? ''}
                onChange={handleOptionalStringChange('renewalDate')}
                className={`input ${errors.renewalDate ? 'border-red-500' : ''}`}
              />
              {errors.renewalDate && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.renewalDate}</p>
              )}
            </div>
          </>
        );

      case AccountType.SAVINGS:
        return (
          <>
            <div>
              <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Interest Rate (%)
              </label>
              <input
                type="number"
                id="interestRate"
                value={formData.interestRate ?? ''}
                onChange={handleNumericInputChange('interestRate')}
                className={`input ${errors.interestRate ? 'border-red-500' : ''}`}
                placeholder="Enter interest rate"
                step="0.01"
                min="0"
                max="100"
              />
              {errors.interestRate && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.interestRate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="interestCalculatedDay" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Interest Calculated Day
              </label>
              <input
                type="number"
                id="interestCalculatedDay"
                value={formData.interestCalculatedDay ?? ''}
                onChange={handleNumericInputChange('interestCalculatedDay')}
                className={`input ${errors.interestCalculatedDay ? 'border-red-500' : ''}`}
                placeholder="Day of month (1-31)"
                min="1"
                max="31"
              />
              {errors.interestCalculatedDay && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.interestCalculatedDay}</p>
              )}
            </div>
          </>
        );

      case AccountType.LOAN:
        return (
          <>
            <div>
              <label htmlFor="loanPurpose" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Loan Purpose
              </label>
              <div className="relative">
                <select
                  id="loanPurpose"
                  value={formData.loanPurpose ?? ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleInputChange(
                      'loanPurpose',
                      value === '' ? undefined : (value as LoanPurpose)
                    );
                    if (value !== LoanPurpose.NOVATED_LEASE) {
                      // Clear novated-only fields when switching away
                      setFormData(prev => ({
                        ...prev,
                        loanPurpose: value === '' ? undefined : (value as LoanPurpose),
                        leaseEndDate: value === LoanPurpose.NOVATED_LEASE ? prev.leaseEndDate : undefined,
                        residualValue: value === LoanPurpose.NOVATED_LEASE ? prev.residualValue : undefined,
                        employerName: value === LoanPurpose.NOVATED_LEASE ? prev.employerName : undefined,
                      }));
                    }
                  }}
                  className={`w-full px-3 py-2 pr-10 border ${errors.loanPurpose ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer`}
                >
                  <option value="">Select purpose (optional)</option>
                  {Object.values(LoanPurpose).map((purpose) => (
                    <option key={purpose} value={purpose}>
                      {LOAN_PURPOSE_LABELS[purpose]}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Use Novated Lease for salary-sacrificed vehicles.
              </p>
            </div>

            {formData.loanPurpose === LoanPurpose.NOVATED_LEASE && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm text-blue-800 dark:text-blue-200">
                <Info className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
                <p>
                  Pre-tax lease payments via your employer do not need to be tracked as transactions.
                  Only add personal out-of-pocket costs (fuel, tolls, etc.) under Transport categories.
                  Link a vehicle asset and enter remaining obligation if known; leave balance at $0 if unknown.
                </p>
              </div>
            )}

            <div>
              <label htmlFor="loanAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {formData.loanPurpose === LoanPurpose.NOVATED_LEASE
                  ? 'Remaining Lease Obligation'
                  : 'Loan Amount'}
              </label>
              <input
                type="number"
                id="loanAmount"
                value={formData.loanAmount ?? ''}
                onChange={handleNumericInputChange('loanAmount')}
                className={`input ${errors.loanAmount ? 'border-red-500' : ''}`}
                placeholder={
                  formData.loanPurpose === LoanPurpose.NOVATED_LEASE
                    ? 'Estimated remaining balance (or 0 if unknown)'
                    : 'Enter loan amount'
                }
                step="0.01"
                min="0"
              />
              {errors.loanAmount && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.loanAmount}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="interestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Interest Rate (%)
              </label>
              <input
                type="number"
                id="interestRate"
                value={formData.interestRate ?? ''}
                onChange={handleNumericInputChange('interestRate')}
                className={`input ${errors.interestRate ? 'border-red-500' : ''}`}
                placeholder={
                  formData.loanPurpose === LoanPurpose.NOVATED_LEASE
                    ? 'Enter 0 if not applicable'
                    : 'Enter interest rate'
                }
                step="0.01"
                min="0"
                max="100"
              />
              {errors.interestRate && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.interestRate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="paymentDueDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Payment Due Date
              </label>
              <input
                type="date"
                id="paymentDueDate"
                value={formData.paymentDueDate}
                onChange={(e) => handleInputChange('paymentDueDate', e.target.value)}
                className={`input ${errors.paymentDueDate ? 'border-red-500' : ''}`}
              />
              {errors.paymentDueDate && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.paymentDueDate}</p>
              )}
            </div>
            
            <div>
              <label htmlFor="lastPaymentDue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Last Payment Due
              </label>
              <input
                type="date"
                id="lastPaymentDue"
                value={formData.lastPaymentDue ?? ''}
                onChange={handleOptionalStringChange('lastPaymentDue')}
                className={`input ${errors.lastPaymentDue ? 'border-red-500' : ''}`}
              />
              {errors.lastPaymentDue && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.lastPaymentDue}</p>
              )}
            </div>

            {formData.loanPurpose === LoanPurpose.NOVATED_LEASE && (
              <>
                <div>
                  <label htmlFor="employerName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Employer Name
                  </label>
                  <input
                    type="text"
                    id="employerName"
                    value={formData.employerName ?? ''}
                    onChange={handleOptionalStringChange('employerName')}
                    className={`input ${errors.employerName ? 'border-red-500' : ''}`}
                    placeholder="Employer managing salary sacrifice"
                  />
                  {errors.employerName && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.employerName}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="leaseEndDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Lease End Date
                  </label>
                  <input
                    type="date"
                    id="leaseEndDate"
                    value={formData.leaseEndDate ?? ''}
                    onChange={handleOptionalStringChange('leaseEndDate')}
                    className={`input ${errors.leaseEndDate ? 'border-red-500' : ''}`}
                  />
                  {errors.leaseEndDate && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.leaseEndDate}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="residualValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Residual / Balloon Value
                  </label>
                  <input
                    type="number"
                    id="residualValue"
                    value={formData.residualValue ?? ''}
                    onChange={handleNumericInputChange('residualValue')}
                    className={`input ${errors.residualValue ? 'border-red-500' : ''}`}
                    placeholder="Optional residual value at lease end"
                    step="0.01"
                    min="0"
                  />
                  {errors.residualValue && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.residualValue}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="linkedAccountId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Linked Vehicle Asset
                  </label>
                  <div className="relative">
                    <select
                      id="linkedAccountId"
                      value={formData.linkedAccountId ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange('linkedAccountId', value === '' ? null : value);
                      }}
                      className={`w-full px-3 py-2 pr-10 border ${errors.linkedAccountId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer`}
                    >
                      <option value="">None / not linked</option>
                      {vehicleAssets.map((asset) => (
                        <option key={asset.id} value={asset.id}>
                          {asset.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  </div>
                  {vehicleAssets.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      No vehicle assets found. Add the car as an Assets account first to link it.
                    </p>
                  )}
                  {errors.linkedAccountId && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.linkedAccountId}</p>
                  )}
                </div>
              </>
            )}
          </>
        );

      case AccountType.ASSETS:
        return (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <div className="relative">
                  <select
                    id="category"
                    value={formData.category ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const category = value === '' ? undefined : (value as AssetCategory);
                      handleInputChange('category', category);
                    }}
                    className={`w-full px-3 py-2 pr-10 border ${errors.category ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer`}
                    required
                  >
                    <option value="">Select category</option>
                    {Object.values(AssetCategory).map(cat => (
                      <option key={cat} value={cat}>
                        {cat.charAt(0).toUpperCase() + cat.slice(1).replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
                {errors.category && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.category}</p>
                )}
              </div>

              {formData.category && (
                <div>
                  <label htmlFor="subType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Subcategory *
                  </label>
                  <div className="relative">
                    <select
                      id="subType"
                      value={formData.subType ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        const subType = value === '' ? undefined : (value as AssetSubType);
                        handleInputChange('subType', subType);
                      }}
                      className={`w-full px-3 py-2 pr-10 border ${errors.subType ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer`}
                      required
                    >
                      <option value="">Select subcategory</option>
                      {availableSubTypes.map(subType => (
                        <option key={subType} value={subType}>
                          {subType.charAt(0).toUpperCase() + subType.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                  </div>
                  {errors.subType && (
                    <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.subType}</p>
                  )}
                </div>
              )}
            </div>

            {/* Dividing line between Category/Subcategory and Valuation fields */}
            <div className="border-t border-gray-200 dark:border-gray-700 my-4"></div>

            {/* Valuation fields in 2-column layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Row 1: Current Value | Last Valuation Date */}
              <div>
                <label htmlFor="currentValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Current Value *
                </label>
                <input
                  type="number"
                  id="currentValue"
                  value={formData.currentValue ?? ''}
                  onChange={handleNumericInputChange('currentValue')}
                  className={`input ${errors.currentValue ? 'border-red-500' : ''}`}
                  placeholder="Enter current value"
                  step="0.01"
                  required
                />
                {errors.currentValue && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.currentValue}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastValuationDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Last Valuation Date (Optional)
                </label>
                <input
                  type="date"
                  id="lastValuationDate"
                  value={formData.lastValuationDate ?? formData.purchaseDate ?? formData.openingDate ?? ''}
                  onChange={handleOptionalStringChange('lastValuationDate')}
                  className={`input ${errors.lastValuationDate ? 'border-red-500' : ''}`}
                />
                {errors.lastValuationDate && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.lastValuationDate}</p>
                )}
              </div>

              {/* Row 2: Valuation Source | Valuation Provider */}
              <div>
                <label htmlFor="valuationSource" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valuation Source
                </label>
                <div className="relative">
                  <select
                    id="valuationSource"
                    value={formData.valuationSource ?? ValuationSource.MANUAL}
                    onChange={(e) => handleInputChange('valuationSource', e.target.value as ValuationSource)}
                    className={`w-full px-3 py-2 pr-10 border ${errors.valuationSource ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer`}
                  >
                    {Object.values(ValuationSource).map(source => (
                      <option key={source} value={source}>
                        {source.charAt(0).toUpperCase() + source.slice(1).replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" aria-hidden="true" />
                </div>
                {errors.valuationSource && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.valuationSource}</p>
                )}
              </div>

              <div>
                <label htmlFor="valuationProvider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Valuation Provider (Optional)
                </label>
                <input
                  type="text"
                  id="valuationProvider"
                  value={formData.valuationProvider ?? ''}
                  onChange={handleOptionalStringChange('valuationProvider')}
                  className={`input ${errors.valuationProvider ? 'border-red-500' : ''}`}
                  placeholder="e.g., ABC Appraisals, State Farm Insurance"
                  maxLength={255}
                />
                {errors.valuationProvider && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.valuationProvider}</p>
                )}
              </div>
            </div>

            {/* Note: Purchase Date and Purchase Price are shown in the basic fields section */}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Validation Error Summary */}
      {Object.keys(errors).length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <svg className="h-5 w-5 text-red-800 dark:text-red-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                Please fix the following errors:
              </p>
              <ul className="list-disc list-inside text-sm text-red-700 dark:text-red-300 space-y-1">
                {Object.entries(errors).map(([field, message]) => (
                  <li key={field}>
                    <span className="font-medium capitalize">{field.replace(/([A-Z])/g, ' $1').trim()}:</span> {message}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Account Type */}
      <AccountTypeSelector
        value={formData.type}
        onChange={(type) => handleInputChange('type', type)}
        disabled={loading}
        error={errors.type}
      />

      {/* Basic Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {formData.type === AccountType.ASSETS ? 'Asset Name *' : 'Account Name *'}
          </label>
          <input
            type="text"
            id="name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            className={`input ${errors.name ? 'border-red-500' : ''}`}
            placeholder={formData.type === AccountType.ASSETS ? "Enter asset name" : "Enter account name"}
            required
          />
          {errors.name && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.name}</p>
          )}
        </div>

        <div>
          <label htmlFor="provider" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {formData.type === AccountType.ASSETS ? 'Supplier *' : 'Institution/Provider *'}
          </label>
          <input
            type="text"
            id="provider"
            value={formData.provider}
            onChange={(e) => handleInputChange('provider', e.target.value)}
            className={`input ${errors.provider ? 'border-red-500' : ''}`}
            placeholder={formData.type === AccountType.ASSETS ? "Enter supplier name" : "Enter bank or institution name"}
            required
          />
          {errors.provider && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.provider}</p>
          )}
        </div>

        {/* Hide Account Number for assets */}
        {formData.type !== AccountType.ASSETS && (
          <div>
            <label htmlFor="accountNumber" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Account Number (Last 4 digits) *
            </label>
            <input
              type="text"
              id="accountNumber"
              value={formData.accountNumber}
              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
              className={`input ${errors.accountNumber ? 'border-red-500' : ''}`}
              placeholder="1234"
              maxLength={4}
              pattern="[0-9]{4}"
              required
            />
            {errors.accountNumber && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.accountNumber}</p>
            )}
          </div>
        )}

        {/* Hide Opening Balance for assets (we use currentValue instead) */}
        {formData.type !== AccountType.ASSETS && (
          <div>
            <label htmlFor="openingBalance" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Opening Balance *
            </label>
            <input
              type="number"
              id="openingBalance"
              value={formData.openingBalance}
              onChange={handleRequiredNumberChange('openingBalance')}
              className={`input ${errors.openingBalance ? 'border-red-500' : ''}`}
              placeholder="0.00"
              step="0.01"
              required
            />
            {errors.openingBalance && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.openingBalance}</p>
            )}
          </div>
        )}

        <div>
          <label htmlFor="openingDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {formData.type === AccountType.ASSETS ? 'Purchase Date *' : 'Opening Date *'}
          </label>
            <input
            type="date"
            id="openingDate"
            value={formData.type === AccountType.ASSETS ? (formData.purchaseDate ?? formData.openingDate) : formData.openingDate}
            onChange={(e) => {
              const dateValue = e.target.value;
              if (formData.type === AccountType.ASSETS) {
                // Purchase date is the source of truth for assets; keep both fields in sync
                handleInputChange('purchaseDate', dateValue);
                handleInputChange('openingDate', dateValue);
              } else {
                handleInputChange('openingDate', dateValue);
              }
            }}
            className={`input ${(errors.openingDate || errors.purchaseDate) ? 'border-red-500' : ''}`}
            required
          />
          {(errors.openingDate || errors.purchaseDate) && (
            <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.purchaseDate || errors.openingDate}</p>
          )}
        </div>

        {/* Purchase Price - only show for assets, next to Purchase Date */}
        {formData.type === AccountType.ASSETS && (
          <div>
            <label htmlFor="purchasePrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Purchase Price (Optional)
            </label>
            <input
              type="number"
              id="purchasePrice"
              value={formData.purchasePrice ?? ''}
              onChange={handleNumericInputChange('purchasePrice')}
              className={`input ${errors.purchasePrice ? 'border-red-500' : ''}`}
              placeholder="Enter purchase price"
              step="0.01"
              min="0"
            />
            {errors.purchasePrice && (
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.purchasePrice}</p>
            )}
          </div>
        )}
      </div>

      {/* Type-specific Fields */}
      {renderTypeSpecificFields()}

      {/* Net Worth Inclusion */}
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="includeInNetWorth"
          checked={formData.includeInNetWorth}
          onChange={(e) => handleInputChange('includeInNetWorth', e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="includeInNetWorth" className="text-sm text-gray-700 dark:text-gray-300">
          Include in Net Worth calculation
        </label>
      </div>

      {/* Notes */}
      <div>
        <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Notes (Optional)
        </label>
        <textarea
          id="notes"
          value={formData.notes}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          className={`input ${errors.notes ? 'border-red-500' : ''}`}
          placeholder="Add any additional notes about this account..."
          rows={3}
        />
        {errors.notes && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-1">{errors.notes}</p>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          className="btn-secondary btn-md"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary btn-md"
          disabled={loading}
          data-loading={loading ? 'true' : undefined}
        >
          {loading ? 'Saving...' : (isEdit ? 'Update Account' : 'Create Account')}
        </button>
      </div>
    </form>
  );
}
